import { useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import moment from "moment";
import { useHistory, useParams } from "react-router";

import { Badge, Button, Card, Col, Nav, Row, Spinner, Tab } from "react-bootstrap";
import { EventBaseDataConfig } from "./EventBaseData";
import { ChainedOfferEventStatus } from "../../../../__gqltypes__";
import { LoadingLogo } from "../../devtools/components/Modal";
import AddModal from "./AddModal";

type EventBaseDesc = {
  id: string;
} & EventBaseDataConfig;

type Props = {
  eventsLoading: boolean,
  loading: boolean,
  events: Array<EventBaseDesc>,
  title: string,
  baseURL: string,
  eventComponent: (id: string) => JSX.Element,
  onAddEvent: (value: { name: string }) => void | Promise<string | null>,
}

export function EventTabContainer({
  eventsLoading,
  loading,
  events,
  title,
  baseURL,
  eventComponent,
  onAddEvent,
}: Props): JSX.Element {
  const { id: eventId } = useParams<{ id: string }>();
      const history = useHistory();
  const [showAddModal, setShowAddModal] = useState(false);
  const selectEvent = useCallback((newId) => {
    history.push(`${baseURL}/${newId}`);
  }, [baseURL, history]);
    
  const isEventSelected = useMemo(() => {
    return eventId && eventId.length > 0 && events.find(({id}) => id === eventId);
  }, [eventId, events]);

  const component = useMemo(() => {
    return eventComponent(eventId);
  }, [eventComponent, eventId]);
  
  useEffect(() => {
      if (eventId === ':id') {
      selectEvent('');
    }
    if (!isEventSelected && events.length > 0) {
      selectEvent(events[0].id);
    }

  }, [isEventSelected, selectEvent, eventId, events]);

  const onAddEventWrapper = useCallback((value: Parameters<typeof onAddEvent>[0]) => {
    const res = onAddEvent(value);
    if (typeof res === 'object' && typeof res.then === 'function') {
      res.then((result) => {
        if (typeof result === 'string') {
          // let's do auto select when onAddEvent returns id
          selectEvent(result);
        }
      });
    }
  }, [onAddEvent, selectEvent]);

  const _now = moment();
  return (
    <>
    <LoadingLogo show={eventsLoading} />
    {isEventSelected && (
      // @ts-ignore
      <Tab.Container className="h-100" activeKey={eventId} onSelect={selectEvent} >
        <Row className="mb-5 h-100">
          <Col className="mb-3">
          <Nav.Item className="mb-3">
          { loading ? (
            <Spinner animation="border" />
          ): (
            <>
              <Button className="w-100" variant='success' onClick={() => setShowAddModal(true)}>
                <i className="fas fa-plus mr-2" />
                Create {title}
              </Button>
            </>
          )}
          </Nav.Item>
            {/* <Nav.Item className="mb-3">
              <FlushButton model={RedisCacheModel.CHAINED_OFFER} />
            </Nav.Item> */}
            <Nav variant='pills'>
              {events.map((eventData) => (
                <Nav.Item key={eventData.id} className="w-100 position-relative">
                  <Nav.Link
                    eventKey={eventData.id}
                    className='d-flex justify-content-between text-break align-items-center'
                  >
                    <span>{eventData.name}</span>
                    <Badge
                      className="m-1"
                      pill
                      variant={
                        eventData.status === ChainedOfferEventStatus.DRAFT
                        ? 'danger'
                        : moment(eventData.startDate).isAfter(_now)
                        ? 'warning'
                        : moment(eventData.stopDate).isBefore(_now)
                        ? 'secondary'
                        : 'success'
                      }>
                        {
                        eventData.status === ChainedOfferEventStatus.DRAFT
                        ? 'Inactive'
                        : moment(eventData.startDate).isAfter(_now)
                        ? 'Pending'
                        : moment(eventData.stopDate).isBefore(_now)
                        ? 'Expired'
                        : 'Running'}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </Col>

          <Col className="col-10">
            <Tab.Content className='h-100'>
              <Card>
                <Card.Body>
                  {component}
                </Card.Body>

              </Card>
            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>
    )}
    <AddModal
      title={`${title} name`}
      show={showAddModal || (!eventsLoading && events.length === 0)}
      loading={loading}
      onHide={() => setShowAddModal(false)}
      onAdd={onAddEventWrapper}
    />
  </>
  );
}